// src/components/Canvas/Toolbar.tsx
import React, { useState, useRef } from 'react';
import { ToolType, BackgroundType, LineStyle } from './types';

interface ToolbarProps {
  currentTool: ToolType;
  onToolChange: (tool: ToolType) => void;
  penColor: string;
  onColorChange: (color: string) => void;
  penWidth: number;
  onWidthChange: (width: number) => void;
  lineStyle: LineStyle;
  onLineStyleChange: (style: LineStyle) => void;
  onLineStylesChange: (style: LineStyle) => void;
  backgroundType: BackgroundType;
  onBackgroundChange: (type: BackgroundType) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  selectedLines: string[]; // ID выделенных элементов
  onLineColorChange: (color: string) => void;
  onLineWidthChange: (width: number) => void;
  // Новое свойство для цвета выделенных элементов
  selectionColor: string | null;
  // Новое свойство для ширины выделенных элементов
  selectionWidth: number | null;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  currentTool,
  onToolChange,
  penColor,
  onColorChange,
  penWidth,
  onWidthChange,
  lineStyle,
  onLineStyleChange,
  onLineStylesChange,
  backgroundType,
  onBackgroundChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  selectedLines,
  onLineColorChange,
  onLineWidthChange,
  selectionColor,
  selectionWidth
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSelectedColorPicker, setShowSelectedColorPicker] = useState(false);
  const colorChangeTimeout = useRef<NodeJS.Timeout | null>(null);
  const selectedColorChangeTimeout = useRef<NodeJS.Timeout | null>(null);
  
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (colorChangeTimeout.current) clearTimeout(colorChangeTimeout.current);
    colorChangeTimeout.current = setTimeout(() => onColorChange(newColor), 50);
  };
  
  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWidth = Number(e.target.value);
    if (colorChangeTimeout.current) clearTimeout(colorChangeTimeout.current);
    colorChangeTimeout.current = setTimeout(() => onWidthChange(newWidth), 50);
  };
  
  const handleSelectedColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (selectedColorChangeTimeout.current) clearTimeout(selectedColorChangeTimeout.current);
    selectedColorChangeTimeout.current = setTimeout(() => onLineColorChange(newColor), 50);
  };
  
  const handleSelectedWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWidth = Number(e.target.value);
    if (selectedColorChangeTimeout.current) clearTimeout(selectedColorChangeTimeout.current);
    selectedColorChangeTimeout.current = setTimeout(() => onLineWidthChange(newWidth), 50);
  };
  
  const isDarkTheme = backgroundType === 'black';
  
  // Определяем текущую ширину для отображения в ползунке
  const currentWidth = selectedLines.length > 0 && selectionWidth !== null ? selectionWidth : penWidth;
  
  return (
    <div className={`toolbar ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <div className="tool-group">
        <button 
          className={currentTool === 'pointer' ? 'active' : ''} 
          onClick={() => onToolChange('pointer')}
          title="Указатель"
        >
          <i className="icon">↖️</i>
        </button>
        <button 
          className={currentTool === 'hand' ? 'active' : ''} 
          onClick={() => onToolChange('hand')}
          title="Рука"
        >
          <i className="icon">✋</i>
        </button>
        <button 
          className={currentTool === 'eraser' ? 'active' : ''} 
          onClick={() => onToolChange('eraser')}
          title="Ластик"
        >
          <i className="icon">🧹</i>
        </button>
        <button 
          className={currentTool === 'pen' ? 'active' : ''} 
          onClick={() => onToolChange('pen')}
          title="Ручка"
        >
          <i className="icon">✏️</i>
        </button>
        <button 
          className={currentTool === 'line' ? 'active' : ''} 
          onClick={() => onToolChange('line')}
          title="Прямая"
        >
          <i className="icon">📏</i>
        </button>
        <button 
          className={currentTool === 'text' ? 'active' : ''} 
          onClick={() => onToolChange('text')}
          title="Текст"
        >
          <i className="icon">📝</i>
        </button>
      </div>
      <div className="tool-group">
        <button 
          onClick={onUndo}
          disabled={!canUndo}
          title="Назад (Ctrl+Z)"
        >
          <i className="icon">↩️</i>
        </button>
        <button 
          onClick={onRedo}
          disabled={!canRedo}
          title="Вперед (Ctrl+Y)"
        >
          <i className="icon">↪️</i>
        </button>
      </div>
      {(currentTool === 'pen' || currentTool === 'line' || currentTool === 'text' || (currentTool === 'pointer' && selectedLines.length > 0)) && (
        <>
          <div className="tool-group">
            {currentTool === 'pointer' && selectedLines.length > 0 ? (
              <div className="color-picker">
                <button 
                  className="color-preview" 
                  // Используем цвет выделенных элементов, если он определен, иначе используем общий цвет
                  style={{ backgroundColor: selectionColor || penColor }}
                  onClick={() => setShowSelectedColorPicker(!showSelectedColorPicker)}
                />
                {showSelectedColorPicker && (
                  <input 
                    type="color" 
                    value={selectionColor || penColor} 
                    onChange={handleSelectedColorChange}
                    onMouseUp={(e) => {
                      if (selectedColorChangeTimeout.current) {
                        clearTimeout(selectedColorChangeTimeout.current);
                      }
                      const target = e.target as HTMLInputElement;
                      onLineColorChange(target.value);
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="color-picker">
                <button 
                  className="color-preview" 
                  style={{ backgroundColor: penColor }}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                />
                {showColorPicker && (
                  <input 
                    type="color" 
                    value={penColor} 
                    onChange={handleColorChange}
                    onMouseUp={(e) => {
                      if (colorChangeTimeout.current) {
                        clearTimeout(colorChangeTimeout.current);
                      }
                      const target = e.target as HTMLInputElement;
                      onColorChange(target.value);
                    }}
                  />
                )}
              </div>
            )}
            <input 
              type="range" 
              min="1" 
              max="20" 
              value={currentWidth} 
              onChange={currentTool === 'pointer' && selectedLines.length > 0 ? handleSelectedWidthChange : handleWidthChange}
              className="width-slider"
              // Используем onInput вместо onMouseMove для плавного обновления
              onInput={(e) => {
                const value = Number((e.target as HTMLInputElement).value);
                if (currentTool === 'pointer' && selectedLines.length > 0) {
                  onLineWidthChange(value);
                } else {
                  onWidthChange(value);
                }
              }}
            />
            <span className="width-value">{currentWidth}px</span>
          </div>
          {(currentTool === 'pen' || currentTool === 'line' || (currentTool === 'pointer' && selectedLines.length > 0)) && (
            <div className="tool-group line-style-group">
              <button 
                className={lineStyle === 'solid' ? 'active' : ''} 
                onClick={() => selectedLines.length > 0 ? onLineStylesChange('solid') : onLineStyleChange('solid')}
                title="Сплошная линия"
              >
                ─
              </button>
              <button 
                className={lineStyle === 'dashed' ? 'active' : ''} 
                onClick={() => selectedLines.length > 0 ? onLineStylesChange('dashed') : onLineStyleChange('dashed')}
                title="Пунктирная линия"
              >
                ┄
              </button>
              <button 
                className={lineStyle === 'dotted' ? 'active' : ''} 
                onClick={() => selectedLines.length > 0 ? onLineStylesChange('dotted') : onLineStyleChange('dotted')}
                title="Точечная линия"
              >
                ⋮
              </button>
            </div>
          )}
        </>
      )}
      <div className="tool-group background-options">
        <button
          className={`background-option ${backgroundType === 'grid' ? 'active' : ''}`}
          onClick={() => onBackgroundChange('grid')}
          title="Тетрадь в клетку"
        >
          <div className="background-preview grid-preview" />
        </button>
        <button
          className={`background-option ${backgroundType === 'black' ? 'active' : ''}`}
          onClick={() => onBackgroundChange('black')}
          title="Черный фон"
        >
          <div className="background-preview black-preview" />
        </button>
      </div>
    </div>
  );
};